/** @jsxImportSource @emotion/react */
import { useState } from 'react'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import Icon from '../Icon'

type ScheduleOption = 'ai' | 'manual'

interface ScheduleEmptyStateProps {
  onGenerateSchedule: () => void
  onManualImport: () => void
}

const titleFont = isCanlan
  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
  : 'Barlow Condensed, Arial, Helvetica, sans-serif'

const bodyFont = isCanlan
  ? 'Gilroy, Arial, Helvetica, sans-serif'
  : 'Inter, Arial, Helvetica, sans-serif'

const ScheduleEmptyState = ({
  onGenerateSchedule,
  onManualImport,
}: ScheduleEmptyStateProps) => {
  const [selectedOption, setSelectedOption] = useState<ScheduleOption>('ai')

  const handleNext = () => {
    if (selectedOption === 'ai') {
      onGenerateSchedule()
    } else {
      onManualImport()
    }
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 32px 0;
        height: 100%;
        width: 100%;
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 16px;
          align-items: center;
          justify-content: center;
          padding: 16px;
          width: 720px;
          max-width: 100%;
        `}
      >
        {/* Header */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 8px;
            align-items: center;
            text-align: center;
            width: 100%;
          `}
        >
          <h1
            css={css`
              font-family: ${titleFont};
              font-size: 32px;
              font-weight: 600;
              line-height: 38.4px;
              color: ${colors.NEUTRAL_0};
              margin: 0;
            `}
          >
            Let&apos;s Create Your Schedule
          </h1>
          <p
            css={css`
              font-family: ${bodyFont};
              font-size: 16px;
              font-weight: 400;
              line-height: 23.2px;
              color: ${colors.NEUTRAL_50};
              margin: 0;
            `}
          >
            Choose how you&apos;d like to generate the games for this
            competition.
          </p>
        </div>

        {/* Option Cards */}
        <div
          css={css`
            display: flex;
            flex-wrap: wrap;
            gap: 16px;
            width: 100%;
          `}
        >
          {/* AI Scheduler */}
          <div
            role='button'
            tabIndex={0}
            onClick={() => setSelectedOption('ai')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                setSelectedOption('ai')
              }
            }}
            css={css`
              flex: 1 0 0;
              min-width: 200px;
              height: 275px;
              display: flex;
              flex-direction: column;
              gap: 16px;
              align-items: center;
              justify-content: center;
              padding: 40px 24px;
              border-radius: 16px;
              cursor: pointer;
              border: 1px solid
                ${selectedOption === 'ai'
                  ? colors.PRIMARY_DARK
                  : colors.SECONDARY_300};
              background-color: ${selectedOption === 'ai'
                ? colors.PRIMARY
                : colors.SECONDARY_400};
              transition: background-color 0.2s ease, border-color 0.2s ease;
            `}
          >
            <div
              css={css`
                background-color: ${colors.SECONDARY_300};
                border-radius: 40px;
                padding: 16px;
                display: flex;
                align-items: center;
              `}
            >
              <Icon
                name='magic'
                fontSize={40}
                color={
                  selectedOption === 'ai' ? colors.PRIMARY : colors.NEUTRAL_0
                }
              />
            </div>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 8px;
                text-align: center;
                width: 100%;
              `}
            >
              <p
                css={css`
                  font-family: ${titleFont};
                  font-size: 24px;
                  font-weight: 600;
                  line-height: 28.8px;
                  color: ${selectedOption === 'ai'
                    ? colors.PRIMARY_TEXT
                    : colors.NEUTRAL_0};
                  margin: 0;
                `}
              >
                AI Scheduler
              </p>
              <p
                css={css`
                  font-family: ${bodyFont};
                  font-size: 14px;
                  font-weight: 400;
                  line-height: 20.3px;
                  color: ${selectedOption === 'ai'
                    ? colors.NEUTRAL_600
                    : colors.NEUTRAL_50};
                  margin: 0;
                `}
              >
                Automatically generate a balanced schedule using AI.
              </p>
            </div>
          </div>

          {/* Manual Add/Import */}
          <div
            role='button'
            tabIndex={0}
            onClick={() => setSelectedOption('manual')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                setSelectedOption('manual')
              }
            }}
            css={css`
              flex: 1 0 0;
              min-width: 200px;
              height: 275px;
              display: flex;
              flex-direction: column;
              gap: 16px;
              align-items: center;
              justify-content: center;
              padding: 40px 24px;
              border-radius: 16px;
              cursor: pointer;
              border: 1px solid
                ${selectedOption === 'manual'
                  ? colors.PRIMARY_DARK
                  : colors.SECONDARY_300};
              background-color: ${selectedOption === 'manual'
                ? colors.PRIMARY
                : colors.SECONDARY_400};
              transition: background-color 0.2s ease, border-color 0.2s ease;
            `}
          >
            <div
              css={css`
                background-color: ${colors.SECONDARY_300};
                border-radius: 40px;
                padding: 16px;
                display: flex;
                align-items: center;
              `}
            >
              <Icon
                name='file-upload'
                fontSize={40}
                color={
                  selectedOption === 'manual'
                    ? colors.PRIMARY
                    : colors.NEUTRAL_0
                }
              />
            </div>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 8px;
                text-align: center;
                width: 100%;
              `}
            >
              <p
                css={css`
                  font-family: ${titleFont};
                  font-size: 24px;
                  font-weight: 600;
                  line-height: 28.8px;
                  color: ${selectedOption === 'manual'
                    ? colors.PRIMARY_TEXT
                    : colors.NEUTRAL_0};
                  margin: 0;
                `}
              >
                Manual Add/Import
              </p>
              <p
                css={css`
                  font-family: ${bodyFont};
                  font-size: 14px;
                  font-weight: 400;
                  line-height: 20.3px;
                  color: ${selectedOption === 'manual'
                    ? colors.NEUTRAL_600
                    : colors.NEUTRAL_50};
                  margin: 0;
                `}
              >
                Create the schedule manually or
                <br />
                import an existing one.
              </p>
            </div>
          </div>
        </div>

        {/* NEXT Button */}
        <button
          onClick={handleNext}
          type='button'
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            padding: 14px 0;
            background-color: ${colors.PRIMARY};
            border: none;
            border-radius: 16px;
            cursor: pointer;
            font-family: ${titleFont};
            font-size: 24px;
            font-weight: 600;
            line-height: 28.8px;
            color: ${colors.PRIMARY_TEXT};

            &:hover {
              background-color: ${colors.PRIMARY_HOVER};
            }
          `}
        >
          NEXT
        </button>
      </div>
    </div>
  )
}

export default ScheduleEmptyState
